import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Clock, Newspaper, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/admin/PageHeader";
import { Toolbar, Segmented } from "@/components/admin/Toolbar";
import { StatusPill } from "@/components/admin/StatusPill";
import { EmptyState } from "@/components/admin/states/EmptyState";
import { ErrorState } from "@/components/admin/states/ErrorState";
import { adminPosts, imgUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const today = () => new Date().toISOString().slice(0, 10);

/** One story as a visual card: cover (or ink tile), status, title, excerpt, date, reading time, stikkord. */
const PostCard = ({ p, onOpen, i }) => {
  const scheduled = p.status === "published" && (p.published_at || "").slice(0, 10) > today();
  return (
    <li className="reveal-up" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
      <button type="button" onClick={onOpen} className="post-card group" data-testid={`post-row-${p.id}`}>
        <span className="post-card-media">
          {p.cover ? <img src={imgUrl(p.cover, 640)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /> : <span className="post-card-blank"><Newspaper className="h-6 w-6" /></span>}
          <span className="absolute left-3 top-3 flex items-center gap-1.5">
            <StatusPill status={p.status === "published" ? "publisert" : "utkast"} />
            {scheduled && <span className="inline-flex h-6 items-center gap-1 rounded-full bg-paper/90 px-2 text-[11px] font-medium text-ink backdrop-blur" data-testid="post-scheduled-pill"><CalendarClock className="h-3 w-3" /> Planlagd</span>}
          </span>
        </span>
        <span className="flex min-w-0 flex-1 flex-col px-4 pb-4 pt-3.5">
          <span className="line-clamp-2 font-display text-[16.5px] font-semibold leading-[1.2] tracking-[-0.015em]">{p.title || "Utan tittel"}</span>
          {p.excerpt && <span className="mt-1.5 line-clamp-2 text-[13px] leading-snug text-muted-foreground">{p.excerpt}</span>}
          <span className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-[12px] text-muted-foreground">
            <span className="tabular">{fmtDate((p.published_at || p.updated_at || "").slice(0, 10), "d. MMM yyyy")}</span>
            {p.reading_minutes && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{p.reading_minutes} min</span>}
            {p.tags?.length > 0 && <span className="truncate capitalize">{p.tags.slice(0, 3).join(" · ")}</span>}
          </span>
        </span>
      </button>
    </li>
  );
};

/** Admin → Nyheiter: every story, published or draft, newest first. */
export default function AdminPosts() {
  const nav = useNavigate();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-posts"], queryFn: adminPosts });
  const [filter, setFilter] = useState("alle");
  const [q, setQ] = useState("");
  const items = useMemo(() => (data || []).filter((p) => (filter === "alle" || p.status === filter) && (!q || `${p.title} ${(p.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()))).sort((a, b) => (b.published_at || b.updated_at || "").localeCompare(a.published_at || a.updated_at || "")), [data, filter, q]);
  const counts = { published: (data || []).filter((p) => p.status === "published").length, draft: (data || []).filter((p) => p.status === "draft").length };
  if (isError) return <div><PageHeader title="Nyheiter" /><ErrorState title="Kunne ikkje hente sakene" text={error?.message} onRetry={refetch} /></div>;
  return (
    <div data-testid="admin-posts">
      <PageHeader title="Nyheiter" description="«Nytt frå huset» på nettsida. Skriv sjølv eller la AI lage eit utkast frå stikkord – alt kan redigerast før du publiserer."
        actions={<>
          <Button asChild variant="outline" className="h-9 bg-card" data-testid="posts-new-ai-button"><Link to="/admin/nytt/ny?ai=1"><Sparkles className="h-4 w-4 text-sol" /> Skriv med AI</Link></Button>
          <Button asChild className="h-9" data-testid="posts-new-button"><Link to="/admin/nytt/ny"><Plus className="h-4 w-4" /> Ny sak</Link></Button>
        </>} />
      <Toolbar search={q} onSearch={setQ} searchPlaceholder="Søk i tittel eller stikkord…" count={items.length} countLabel="saker" searchTestId="posts-search-input">
        <Segmented size="sm" value={filter} onChange={setFilter} items={[{ value: "alle", label: "Alle" }, { value: "published", label: "Publiserte", count: counts.published }, { value: "draft", label: "Utkast", count: counts.draft }]} testId="posts-filter" />
      </Toolbar>
      {isLoading ? <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div> : !items.length ? (
        <EmptyState icon={Newspaper} title={data?.length ? "Ingen treff" : "Ingen saker enno"} text={data?.length ? "Prøv eit anna søk eller filter." : "Skriv den første saka frå huset – eller gje AI nokre stikkord og få eit ferdig utkast på eit halvt minutt."} action={!data?.length && <Button asChild data-testid="posts-empty-new"><Link to="/admin/nytt/ny"><Sparkles className="h-4 w-4" /> Skriv første sak</Link></Button>} testId="posts-empty" />
      ) : (
        <ul className={cn("grid gap-4 sm:grid-cols-2 xl:grid-cols-3")} data-testid="posts-list">
          {items.map((p, i) => <PostCard key={p.id} p={p} i={i} onOpen={() => nav(`/admin/nytt/${p.id}`)} />)}
        </ul>
      )}
    </div>
  );
}
